import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/ui';
import { taskcontants } from '@/constants/taskConstants';
import { dashboardQueryKeys } from '@/hooks/dashboard/queries';
import { getMoveLocationList, handleCompleteCallAsync } from '@/service/MoveTaskService';
import { cn } from '@/utils/cn';

import type {
  CreateMoveTaskUpdateTaskDetails,
  MoveLocationDetail,
} from '@/service/types/movetasktypes';

// ─────────────────────────────────────────────────────────────────
// Location Picker Dialog
// ─────────────────────────────────────────────────────────────────

interface LocationPickerDialogProps {
  open: boolean;
  task: CreateMoveTaskUpdateTaskDetails | null;
  onClose: () => void;
  onSuccess: () => void;
  allMoveTrailerList: CreateMoveTaskUpdateTaskDetails[];
}

export function LocationPickerDialog({
  open,
  task,
  onClose,
  onSuccess,
  allMoveTrailerList,
}: LocationPickerDialogProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Location list state
  const [locations, setLocations] = useState<MoveLocationDetail[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<MoveLocationDetail | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(true);
  const [taskComments, setTaskComments] = useState('');
  const [trailerComments, setTrailerComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Fetch locations when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedLocation(null);
      setDropdownOpen(true);
      setTaskComments('');
      setTrailerComments('');
      setIsSubmitting(false);
      setValidationError('');
      setLocationsLoading(true);
      void getMoveLocation();
    }
  }, [open]);

  async function getMoveLocation() {
    try {
      const data = await getMoveLocationList(taskcontants.LocationListType);
      setLocations(data);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    const q = searchQuery.toLowerCase().trim();
    return locations.filter(
      (l) =>
        l.LocationName.toLowerCase().includes(q) ||
        l.LocationZone.toLowerCase().includes(q) ||
        l.LocationType.toLowerCase().includes(q)
    );
  }, [locations, searchQuery]);

  async function handleComplete() {
    if (!selectedLocation || !task || isSubmitting) return;

    // Validate destination is different from source
    if (
      selectedLocation.LocationName === task.MoveSourceName &&
      selectedLocation.LocationZone === task.MoveSourceZone
    ) {
      setValidationError(t('locationPicker.sameSourceDestination'));
      return;
    }

    setValidationError('');
    setIsSubmitting(true);

    try {
      const currentTask = allMoveTrailerList.find((x) => x.TrailerName === task.TrailerName);

      if (currentTask) {
        const updatedTask = {
          ...currentTask,
          MoveDestinationName: selectedLocation.LocationName,
          MoveDestinationType: selectedLocation.LocationType,
          MoveDestinationZone: selectedLocation.LocationZone,
          TaskComments: taskComments,
          TrailerComments: trailerComments,
        };
        const response = await handleCompleteCallAsync(updatedTask);

        const bodyStatus = response.Payload.Body.Status.toUpperCase();

        if (bodyStatus === taskcontants.SuccessStatus.toUpperCase()) {
          showToast(t('createMoveTask.moveTaskCreatedSuccess'), { type: 'success' });
          onClose();
          onSuccess();
          // The CREATE endpoint auto-assigns the task (status Assigned/30),
          // so no separate accept call is needed. Invalidate dashboard queries
          // immediately so My Tasks reflects the newly created task.
          void queryClient.invalidateQueries({
            queryKey: dashboardQueryKeys.all,
            refetchType: 'all',
          });
          return;
        } else {
          const updateDetails = response.Payload.Body.UpdateTaskDetails;
          const detailMsg = updateDetails.length > 0 ? updateDetails[0]?.StatusMessage : undefined;
          const errorMsg =
            detailMsg ??
            (response.Payload.Body.StatusMessage || t('createMoveTask.moveTaskFailed'));
          showToast(errorMsg, { type: 'error' });
        }
      }
      onClose();
      onSuccess();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('errors.generic');
      showToast(message, { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!open || !task) return null;

  const sourceLocation = `${task.MoveSourceZone} - ${task.MoveSourceName} (${task.MoveSourceType})`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-screen-md max-h-[80vh] bg-white rounded-2xl overflow-y-auto shadow-xl animate-in zoom-in-95 duration-150 flex flex-col">
        {/* Trailer Info Section */}
        <div className="px-6 pt-6 pb-4">
          {/* Row: Trailer Number & Source Location */}
          <div className="flex justify-between gap-4">
            <div>
              <p className="text-sm font-poppins font-bold text-[#0B1B25]">
                {t('locationPicker.trailerNumber')}
              </p>
              <p className="text-base font-poppins text-[#243742] mt-1">{task.TrailerName}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-poppins font-bold text-[#0B1B25]">
                {t('locationPicker.sourceLocation')}
              </p>
              <p className="text-base font-poppins text-[#243742] mt-1">{sourceLocation}</p>
            </div>
          </div>

          {/* Carrier Name */}
          <div className="mt-4">
            <p className="text-sm font-poppins font-bold text-[#0B1B25]">
              {t('locationPicker.carrierName')}
            </p>
            <p className="text-base font-poppins text-[#243742] mt-1">{task.CarrierName}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-6 border-t border-gray-300" />

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Location Selection Section */}
          <div className="px-6 pt-4">
            <p className="text-sm font-poppins text-[#445E71] mb-2">
              {t('locationPicker.selectLocation')}
            </p>

            {/* Search / Dropdown input */}
            <div className="w-80 rounded border border-gray-300 bg-white">
              <input
                type="text"
                placeholder={t('locationPicker.searchPlaceholder')}
                maxLength={100}
                value={
                  selectedLocation
                    ? `${selectedLocation.LocationZone} - ${selectedLocation.LocationName} (${selectedLocation.LocationType})`
                    : searchQuery
                }
                onFocus={() => {
                  setDropdownOpen(true);
                }}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedLocation(null);
                  setDropdownOpen(true);
                }}
                className="h-10 w-full bg-transparent px-3 text-sm font-poppins text-[#0B1B25] placeholder:text-[#415B6D] outline-none"
              />
            </div>
          </div>

          {/* Location list */}
          {dropdownOpen && (
            <div className="w-[343px] pl-6 pb-2 max-h-[200px] overflow-y-auto">
              {locationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-button" />
                </div>
              ) : filteredLocations.length === 0 ? (
                <p className="text-center text-sm font-poppins text-[#445E71] py-8">
                  {t('locationPicker.noLocationsFound')}
                </p>
              ) : (
                <div className="border border-gray-300">
                  {filteredLocations.map((location) => {
                    const locationKey = `${location.LocationZone}-${location.LocationName}`;
                    const isSelected =
                      selectedLocation?.LocationZone === location.LocationZone &&
                      selectedLocation.LocationName === location.LocationName;
                    return (
                      <button
                        key={locationKey}
                        type="button"
                        onClick={() => {
                          setSelectedLocation(location);
                          setSearchQuery('');
                          setDropdownOpen(false);
                        }}
                        className={cn(
                          'w-full text-left px-3 py-3 text-sm font-poppins text-[#0B1B25] transition-colors',
                          isSelected ? 'bg-gray-200' : 'hover:bg-gray-100'
                        )}
                      >
                        {location.LocationZone} - {location.LocationName} ({location.LocationType})
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Task Comments */}
          <div className="px-6 pt-4">
            <p className="text-sm font-poppins font-bold text-[#0B1B25] mb-2">
              {t('locationPicker.taskComments')}
            </p>
            <textarea
              value={taskComments}
              maxLength={250}
              onChange={(e) => {
                setTaskComments(e.target.value);
              }}
              placeholder={t('locationPicker.taskCommentsPlaceholder')}
              className="w-full h-[80px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-poppins text-[#0B1B25] placeholder:text-[#415B6D] outline-none resize-none"
            />
          </div>

          {/* Trailer Comments */}
          <div className="px-6 pt-4 pb-2">
            <p className="text-sm font-poppins font-bold text-[#0B1B25] mb-2">
              {t('locationPicker.trailerComments')}
            </p>
            <textarea
              value={trailerComments}
              maxLength={250}
              onChange={(e) => {
                setTrailerComments(e.target.value);
              }}
              placeholder={t('locationPicker.trailerCommentsPlaceholder')}
              className="w-full h-[80px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-poppins text-[#0B1B25] placeholder:text-[#415B6D] outline-none resize-none"
            />
          </div>
        </div>

        {/* Validation Error */}
        {validationError && (
          <p className="px-6 text-sm font-poppins text-[#BE0000]">{validationError}</p>
        )}

        {/* Action Buttons */}
        <div className="px-6 py-4 flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="h-10 w-[140px] rounded-md border border-[#003366] text-[#003366] text-xs font-poppins font-normal hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!selectedLocation || isSubmitting}
            onClick={handleComplete}
            className={cn(
              'h-10 w-[140px] rounded-md text-xs font-poppins font-normal transition-colors',
              selectedLocation && !isSubmitting
                ? 'bg-[#2ea650] text-white hover:bg-[#2ea650]'
                : 'bg-gray-300 text-white cursor-not-allowed'
            )}
          >
            {isSubmitting ? t('common.loading') : t('common.complete')}
          </button>
        </div>
      </div>
    </div>
  );
}
